/**
 * BusinessOraculo — el copiloto de tesoreria en el Inicio del dashboard
 * empresarial. Chat solo-lectura: consulta empresa, lotes y aprobaciones
 * pendientes. No ejecuta pagos (eso vive en Pagos, con SMS).
 */
import { useEffect, useRef, useState } from 'react';
import { oraculoBusinessChat, oraculoBusinessContextoVoz } from '../../lib/cerebro';

/**
 * Negritas de verdad, no asteriscos a la vista.
 *
 * El Oraculo escribe "**Para Marina Auli:**" porque su prompt le permite
 * negritas simples, y la burbuja lo pintaba tal cual: el dueno veia los
 * asteriscos. Con una lista de 8 datos por persona, eso pasa de detalle a
 * ruido. No se mete una libreria de markdown por esto: se parte por ** y ya.
 */
function conNegritas(texto) {
  return String(texto || '').split(/(\*\*[^*]+\*\*)/g).map((trozo, i) => (
    trozo.startsWith('**') && trozo.endsWith('**') && trozo.length > 4
      ? <strong key={i}>{trozo.slice(2, -2)}</strong>
      : <span key={i}>{trozo}</span>
  ));
}

const SUGERENCIAS = [
  'Como va mi nomina?',
  'Que espera mi aprobacion?',
  'Cuanto dispersamos este mes?',
];

export default function BusinessOraculo({ pending, onConsumed, retomarVoz, onConvertir }) {
  const [messages, setMessages] = useState([
    { role: 'assistant', content: 'Hola, soy el Oraculo de tu empresa. Preguntame por tu nomina, tus lotes o que espera tu aprobacion.' },
  ]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const endRef = useRef(null);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages, loading]);

  // VENIR DE LA VOZ NO ES EMPEZAR DE CERO. La voz manda al chat cuando hay que
  // escribir datos que no se pueden dictar; si el chat se abre en blanco, el
  // dueno cree que se perdio todo y repite lo que ya dijo — que es exactamente
  // lo que paso en la segunda prueba. Se traen y se PINTAN los ultimos minutos
  // de lo hablado: el backend ademas se los da al modelo como historia.
  useEffect(() => {
    if (!retomarVoz) return;
    let vivo = true;
    (async () => {
      const previos = await oraculoBusinessContextoVoz();
      if (!vivo || !previos.length) return;
      setMessages((prev) => {
        // Solo se retoma sobre un chat virgen: si ya venia escribiendo, su
        // conversacion manda y no se le mete nada por debajo.
        if (prev.length > 1) return prev;
        return [
          ...prev,
          { role: 'system', content: 'Seguimos donde ibamos en la conversacion por voz' },
          ...previos,
        ];
      });
    })();
    return () => { vivo = false; };
  }, [retomarVoz]);

  // Pregunta llegada desde el hero (BusinessOraculoHero): se envia sola una vez.
  useEffect(() => {
    if (pending && pending.trim()) {
      send(pending);
      onConsumed?.();
    }
  }, [pending]); // eslint-disable-line react-hooks/exhaustive-deps

  const send = async (text) => {
    const msg = (text || input).trim();
    if (!msg || loading) return;
    const next = [...messages, { role: 'user', content: msg }];
    setMessages(next);
    setInput('');
    setLoading(true);
    try {
      // El separador de "venimos de la voz" es decoracion de pantalla: al modelo
      // le llegaria como un rol que la API no acepta.
      const history = next.slice(1).filter((m) => m.role !== 'system').slice(-14)
        .map((m) => ({ role: m.role, content: m.content }));
      const result = await oraculoBusinessChat(msg, history.slice(0, -1));
      // Si el Oraculo dejo una accion preparada (ej: conversion cotizada con saldo),
      // el mensaje carga la tarjeta de confirmar — el chat nunca ejecuta solo.
      setMessages((prev) => [...prev, { role: 'assistant', content: result.response, accion: result.accion || null }]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-white border border-[#E5E7EB] rounded-xl p-5 mt-5">
      <div className="flex items-center gap-2 mb-3">
        <span className="w-2 h-2 rounded-full bg-[#c9a961]" />
        <div className="text-sm font-bold text-[#181D27]">El Oraculo de tu empresa</div>
      </div>

      <div className="max-h-96 overflow-y-auto space-y-2 mb-3 pr-1">
        {messages.map((m, i) => (m.role === 'system' ? (
          // Costura entre las dos bocas: que se vea que esto viene de la voz y
          // no es un mensaje del Oraculo.
          <div key={i} className="flex items-center gap-2 py-1">
            <span className="flex-1 h-px bg-[#E5E7EB]" />
            <span className="text-[10px] uppercase tracking-wide text-[#9CA3AF] font-semibold">🎙 {m.content}</span>
            <span className="flex-1 h-px bg-[#E5E7EB]" />
          </div>
        ) : (
          <div key={i} className={`text-sm px-3.5 py-2.5 rounded-xl max-w-[85%] whitespace-pre-wrap ${
            m.role === 'user' ? 'ml-auto bg-[#0a2540] text-white' : 'bg-[#F2F4F7] text-[#181D27]'
          }`}>
            {conNegritas(m.content)}
            {m.accion?.tipo === 'convertir' && onConvertir && (
              <button
                onClick={() => onConvertir(m.accion)}
                className="mt-2 block w-full text-center px-4 py-2 rounded-lg text-white text-xs font-bold"
                style={{ background: 'linear-gradient(135deg, #0A2540, #1A3A6C)', border: '1.5px solid #C9A961' }}
              >
                Confirmar conversión · recibes {m.accion.recibeAprox}
              </button>
            )}
          </div>
        )))}
        {loading && <div className="text-xs text-[#9CA3AF] px-2">El Oraculo esta consultando...</div>}
        <div ref={endRef} />
      </div>

      {messages.length <= 1 && (
        <div className="flex flex-wrap gap-2 mb-3">
          {SUGERENCIAS.map((s) => (
            <button key={s} onClick={() => send(s)} className="text-xs border border-[#E5E7EB] rounded-full px-3 py-1.5 text-[#475467] hover:bg-[#F9FAFB]">
              {s}
            </button>
          ))}
        </div>
      )}

      <div className="flex gap-2">
        <input
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && send()}
          placeholder="Preguntale a tu tesoreria..."
          className="flex-1 border border-[#E5E7EB] rounded-lg px-4 py-2.5 text-sm"
        />
        <button disabled={loading || !input.trim()} onClick={() => send()} className="px-4 py-2.5 rounded-lg bg-[#0a2540] text-white text-sm font-semibold disabled:opacity-50">
          Enviar
        </button>
      </div>
      <div className="text-[10px] text-[#9CA3AF] mt-2">Consulta datos reales de tu empresa. No ejecuta pagos — aprobar siempre eres tu, con tu codigo.</div>
    </div>
  );
}
